import React, { useRef } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { SparklesIcon } from 'lucide-react';
export function Revelation() {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-100px'
  });
  return <section ref={ref} className="py-12 md:py-20 px-4 bg-gradient-to-b from-white to-cream" data-id="element-279">
      <div className="max-w-4xl mx-auto" data-id="element-280">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {
        opacity: 0,
        y: 30
      }} transition={{
        duration: 0.6
      }} data-id="element-281">
          <div className="flex items-center justify-center gap-2 md:gap-3 mb-6 md:mb-8 px-2" data-id="element-282">
            <SparklesIcon className="w-6 h-6 md:w-8 md:h-8 text-terracotta" aria-hidden="true" data-id="element-283" />
            <h2 className="font-heading text-2xl sm:text-3xl md:text-4xl text-warm-brown text-center" data-id="element-284">
              La Gran Revelación
            </h2>
            <SparklesIcon className="w-6 h-6 md:w-8 md:h-8 text-terracotta" aria-hidden="true" data-id="element-285" />
          </div>

          <div className="bg-white rounded-lg shadow-xl p-6 md:p-12 border-2 border-terracotta-light mb-6 md:mb-8" data-id="element-286">
            <h3 className="font-heading text-xl sm:text-2xl md:text-3xl text-gray-900 mb-4 md:mb-6" data-id="element-287">
              Permíteme explicarte algo que nadie te ha contado — y que cambiará
              para siempre cómo ves la salud natural.
            </h3>

            <div className="prose prose-base md:prose-lg max-w-none space-y-3 md:space-y-4 text-gray-700" data-id="element-288">
              <p data-id="element-289">
                Cuando tomas una cápsula de cúrcuma sola, tu hígado la
                identifica como un compuesto extraño y la elimina antes de que
                llegue a tus articulaciones, a tu cerebro o a tu sistema
                digestivo.
              </p>
              <p className="font-semibold text-gray-900" data-id="element-290">
                ¿Cuánto se absorbe realmente?
              </p>
              <div className="bg-terracotta-light/20 border-l-4 border-terracotta p-4 md:p-6 my-4 md:my-6" data-id="element-291">
                <p className="text-2xl md:text-3xl font-bold text-terracotta mb-2" data-id="element-292">
                  Menos del 3%.
                </p>
                <p className="text-gray-900 font-medium" data-id="element-293">
                  Eso significa que el 97% de lo que pagas — literalmente —
                  termina eliminado sin haber hecho nada por ti.
                </p>
              </div>
              <p data-id="element-294">Pero esto no es un fallo de la cúrcuma.</p>
              <p className="text-lg md:text-xl font-bold text-gray-900" data-id="element-295">
                Es un fallo de la preparación.
              </p>
              <p data-id="element-296">
                Los herbalistas ancestrales de Asia, África y América Latina
                nunca usaron una sola planta de forma aislada. Usaron{' '}
                <strong data-id="element-297">sistemas de plantas</strong> — combinaciones precisas
                donde cada ingrediente activa, transporta o potencia al
                siguiente.
              </p>
              <p className="text-lg md:text-xl font-semibold text-sage-dark" data-id="element-298">
                A esto se le llama Sinergia Herbal.
              </p>
            </div>
          </div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {
          opacity: 0,
          y: 20
        }} transition={{
          duration: 0.6,
          delay: 0.3
        }} className="bg-sage-light/30 border-l-4 border-dark-green p-6 md:p-8 rounded-lg mb-6 md:mb-8" data-id="element-299">
            <p className="text-base md:text-lg italic text-gray-800 mb-2 md:mb-3" data-id="element-300">
              "Las combinaciones de plantas pueden mejorar la absorción,
              potenciar los efectos y superar el rendimiento de los ingredientes
              aislados."
            </p>
            <p className="text-sm md:text-base font-semibold text-dark-green" data-id="element-301">
              — Frontiers in Pharmacology, 2022
            </p>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {
          opacity: 0,
          y: 20
        }} transition={{
          duration: 0.6,
          delay: 0.5
        }} className="bg-white rounded-lg shadow-lg p-6 md:p-8 border-2 border-sage" data-id="element-302">
            <h4 className="font-heading text-xl md:text-2xl text-gray-900 mb-3 md:mb-4" data-id="element-303">
              Un ejemplo concreto que puedes aplicar hoy mismo:
            </h4>
            <div className="bg-gradient-to-r from-terracotta-light/20 to-sage-light/20 p-4 md:p-6 rounded-lg" data-id="element-304">
              <p className="text-base md:text-lg text-gray-800 mb-3 md:mb-4" data-id="element-305">
                La cúrcuma combinada con{' '}
                <strong data-id="element-306">pimienta negra y una grasa saludable</strong> —
                exactamente como lo preparaban los herbalistas tradicionales —
                aumenta su absorción hasta un:
              </p>
              <p className="text-4xl md:text-5xl font-bold text-terracotta text-center my-4 md:my-6" data-id="element-307">
                2,000%
              </p>
              <div className="space-y-2 text-gray-700 text-base md:text-lg" data-id="element-308">
                <p data-id="element-309">No es magia.</p>
                <p data-id="element-310">No es folklore.</p>
                <p className="text-lg md:text-xl font-bold text-gray-900 mt-3 md:mt-4" data-id="element-311">
                  Es química ancestral validada por la ciencia moderna.
                </p>
              </div>
            </div>
            <p className="text-base md:text-lg text-gray-700 mt-4 md:mt-6 text-center font-medium" data-id="element-312">
              Y es exactamente lo que encontrarás en cada página de este libro.
            </p>
          </motion.div>
        </motion.div>
      </div>
    </section>;
}